import React, { useState } from 'react';
import { CreditCard, Shield, Clock, Globe, CheckCircle, Copy, QrCode } from 'lucide-react';
import { mockData, mockFormHandlers } from '../mock';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { useToast } from '../hooks/use-toast';

export const CryptoPayment = ({ 
  amount, 
  orderType = 'subscription', 
  orderDetails = {},
  onPaymentCreated = null,
  onPaymentCompleted = null 
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState('');
  const [paymentStep, setPaymentStep] = useState('select'); // select, payment, complete
  const [paymentData, setPaymentData] = useState(null);
  const [customerEmail, setCustomerEmail] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(7200); // 2 hours in seconds
  const { toast } = useToast();

  // Mock crypto payment creation
  const createCryptoPayment = async (currency, email) => {
    const cryptoRates = {
      BTC: 0.0000234, // Example rate: $42,735 per BTC
      ETH: 0.000421,  // Example rate: $2,375 per ETH
      USDT: 1.0,      // 1:1 with USD
      USDC: 1.0,      // 1:1 with USD
      LTC: 0.0154,    // Example rate: $65 per LTC
      BCH: 0.00426    // Example rate: $235 per BCH
    };

    const cryptoAmount = (parseFloat(amount) * cryptoRates[currency]).toFixed(8);
    
    return {
      payment_id: `CP${Date.now()}${Math.random().toString(36).substr(2, 5)}`,
      crypto_address: `${currency}${Math.random().toString(36).substr(2, 34)}`,
      amount_crypto: cryptoAmount,
      amount_usd: amount,
      cryptocurrency: currency,
      expires_at: new Date(Date.now() + 2 * 60 * 60 * 1000), // 2 hours from now
      qr_code_url: `https://chart.googleapis.com/chart?chs=200x200&cht=qr&chl=${currency.toLowerCase()}:${currency}${Math.random().toString(36).substr(2, 34)}?amount=${cryptoAmount}`,
      status: 'pending',
      confirmations: 0,
      required_confirmations: currency === 'BTC' ? 3 : (currency === 'ETH' ? 12 : 6)
    };
  };

  const handlePaymentInitiation = async () => {
    if (!selectedCurrency || !customerEmail) {
      toast({
        title: "Missing Information",
        description: "Please select a cryptocurrency and enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setIsProcessing(true);
    
    try {
      // Simulate API call to create crypto payment
      const payment = await createCryptoPayment(selectedCurrency, customerEmail);
      
      setPaymentData(payment);
      setPaymentStep('payment');
      
      if (onPaymentCreated) {
        onPaymentCreated(payment);
      }

      // Start countdown timer
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      // Simulate payment status checking
      setTimeout(() => {
        setPaymentStep('complete');
        if (onPaymentCompleted) {
          onPaymentCompleted(payment);
        }
        toast({
          title: "Payment Confirmed!",
          description: "Your cryptocurrency payment has been successfully confirmed.",
        });
      }, 10000); // Simulate 10 second confirmation for demo

      toast({
        title: "Payment Created",
        description: `Your ${selectedCurrency} payment address has been generated.`,
      });

    } catch (error) {
      toast({
        title: "Payment Error",
        description: "Failed to create cryptocurrency payment. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: "Address copied to clipboard.",
    });
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (paymentStep === 'select') {
    return (
      <Card style={{
        border: '1px solid var(--border-light)',
        borderRadius: '0px',
        background: 'var(--bg-primary)'
      }}>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'var(--color-gold)',
              borderRadius: '0px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <CreditCard size={24} color="white" />
            </div>
            <div>
              <CardTitle className="heading-2">Cryptocurrency Payment</CardTitle>
              <CardDescription className="body-regular">
                Pay securely with digital currency
              </CardDescription>
            </div>
          </div>
          
          <div style={{
            background: 'var(--color-lavender)',
            padding: '16px',
            borderRadius: '0px',
            marginBottom: '24px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="body-regular">Total Amount:</span>
              <span className="heading-2" style={{ color: 'var(--color-gold)' }}>
                ${amount} USD
              </span>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Email Input */}
          <div style={{ marginBottom: '24px' }}>
            <label className="body-regular" style={{ 
              display: 'block', 
              marginBottom: '8px',
              color: 'var(--text-primary)'
            }}>
              Email Address *
            </label>
            <Input
              type="email"
              value={customerEmail}
              onChange={(e) => setCustomerEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
              style={{ borderRadius: '0px' }}
            />
          </div>

          {/* Currency Selection */}
          <div style={{ marginBottom: '24px' }}>
            <label className="body-regular" style={{ 
              display: 'block', 
              marginBottom: '8px',
              color: 'var(--text-primary)'
            }}>
              Select Cryptocurrency *
            </label>
            
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
              gap: '12px',
              marginBottom: '16px'
            }}>
              {mockData.cryptoPaymentOptions.supportedCurrencies.map((crypto) => (
                <button
                  key={crypto.symbol}
                  onClick={() => setSelectedCurrency(crypto.symbol)}
                  style={{
                    padding: '16px 12px',
                    border: `2px solid ${selectedCurrency === crypto.symbol ? 'var(--color-gold)' : 'var(--border-light)'}`,
                    background: selectedCurrency === crypto.symbol ? 'var(--color-lavender)' : 'transparent',
                    borderRadius: '0px',
                    cursor: 'pointer',
                    textAlign: 'center',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div className="heading-3" style={{ marginBottom: '4px' }}>
                    {crypto.icon} {crypto.symbol}
                  </div>
                  <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {crypto.name}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Payment Features */}
          <div style={{ marginBottom: '32px' }}>
            <h4 className="heading-3" style={{ marginBottom: '16px' }}>Why Pay with Cryptocurrency?</h4>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px' 
            }}>
              {[
                { icon: Shield, text: 'Secure blockchain technology' },
                { icon: Clock, text: '24/7 payment processing' },
                { icon: Globe, text: 'Global accessibility' },
                { icon: CheckCircle, text: 'No chargebacks' }
              ].map((feature, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <feature.icon size={16} color="var(--color-fence-green)" />
                  <span className="body-small" style={{ color: 'var(--text-secondary)' }}>
                    {feature.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <Button 
            onClick={handlePaymentInitiation}
            disabled={!selectedCurrency || !customerEmail || isProcessing}
            className="btn-gold"
            style={{ width: '100%' }}
          >
            {isProcessing ? 'Creating Payment...' : `Pay ${amount} USD with ${selectedCurrency || 'Crypto'}`}
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (paymentStep === 'payment' && paymentData) {
    return (
      <Card style={{
        border: '1px solid var(--border-light)',
        borderRadius: '0px',
        background: 'var(--bg-primary)'
      }}>
        <CardHeader>
          <CardTitle className="heading-2">Complete Your Payment</CardTitle>
          <CardDescription className="body-regular">
            Send exactly {paymentData.amount_crypto} {paymentData.cryptocurrency} to the address below
          </CardDescription>
          
          <div style={{
            background: timeRemaining < 600 ? '#fee2e2' : 'var(--color-lavender)',
            padding: '16px',
            borderRadius: '0px',
            textAlign: 'center'
          }}>
            <div className="body-regular" style={{ marginBottom: '8px' }}>Time Remaining:</div>
            <div className="heading-2" style={{ 
              color: timeRemaining < 600 ? '#dc2626' : 'var(--color-fence-green)' 
            }}>
              {formatTime(timeRemaining)}
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {/* Payment Amount */}
          <div style={{
            background: 'var(--color-fence-green)',
            color: 'white',
            padding: '24px',
            marginBottom: '24px',
            textAlign: 'center'
          }}>
            <div className="body-regular" style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '8px' }}>
              Send Exactly:
            </div>
            <div className="hero-medium" style={{ color: 'white' }}>
              {paymentData.amount_crypto} {paymentData.cryptocurrency}
            </div>
            <div className="body-small" style={{ color: 'rgba(255,255,255,0.8)' }}>
              ≈ ${paymentData.amount_usd} USD
            </div>
          </div>

          {/* Payment Address */}
          <div style={{ marginBottom: '24px' }}>
            <label className="body-regular" style={{ 
              display: 'block', 
              marginBottom: '8px',
              color: 'var(--text-primary)'
            }}>
              {paymentData.cryptocurrency} Address:
            </label>
            <div style={{ 
              display: 'flex', 
              gap: '8px',
              padding: '12px',
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-light)'
            }}>
              <Input
                value={paymentData.crypto_address}
                readOnly
                style={{ 
                  borderRadius: '0px',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}
              />
              <Button
                onClick={() => copyToClipboard(paymentData.crypto_address)}
                variant="ghost"
                style={{ padding: '8px', minWidth: 'auto' }}
              >
                <Copy size={16} />
              </Button>
            </div>
          </div>

          {/* QR Code */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <div className="body-regular" style={{ marginBottom: '12px' }}>
              Scan QR Code to Pay:
            </div>
            <img 
              src={paymentData.qr_code_url} 
              alt="Payment QR Code"
              style={{ 
                border: '1px solid var(--border-light)',
                maxWidth: '200px',
                height: 'auto'
              }}
            />
          </div>

          {/* Payment Instructions */}
          <div style={{
            background: 'var(--bg-secondary)',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <h4 className="heading-3" style={{ marginBottom: '12px' }}>Payment Instructions:</h4>
            <ul style={{ 
              listStyle: 'none', 
              padding: 0,
              margin: 0
            }}>
              <li className="body-small" style={{ 
                marginBottom: '8px',
                paddingLeft: '20px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px',
                  background: 'var(--color-gold)',
                  borderRadius: '50%'
                }}></span>
                Send exactly {paymentData.amount_crypto} {paymentData.cryptocurrency}
              </li>
              <li className="body-small" style={{ 
                marginBottom: '8px',
                paddingLeft: '20px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px',
                  background: 'var(--color-gold)',
                  borderRadius: '50%'
                }}></span>
                Payment must be received within the time limit
              </li>
              <li className="body-small" style={{ 
                marginBottom: '8px',
                paddingLeft: '20px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px',
                  background: 'var(--color-gold)',
                  borderRadius: '50%'
                }}></span>
                Requires {paymentData.required_confirmations} network confirmations
              </li>
              <li className="body-small" style={{ 
                paddingLeft: '20px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px',
                  background: 'var(--color-gold)',
                  borderRadius: '50%'
                }}></span>
                You will receive confirmation once payment is detected
              </li>
            </ul>
          </div>

          <div style={{ textAlign: 'center' }}>
            <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
              Payment ID: {paymentData.payment_id}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (paymentStep === 'complete') {
    return (
      <Card style={{
        border: '2px solid var(--color-fence-green)',
        borderRadius: '0px',
        background: 'var(--bg-primary)'
      }}>
        <CardHeader style={{ textAlign: 'center' }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'var(--color-fence-green)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto'
          }}>
            <CheckCircle size={40} color="white" />
          </div>
          <CardTitle className="heading-1" style={{ color: 'var(--color-fence-green)' }}>
            Payment Confirmed!
          </CardTitle>
          <CardDescription className="body-large">
            Your cryptocurrency payment has been successfully processed
          </CardDescription>
        </CardHeader>

        <CardContent style={{ textAlign: 'center' }}>
          <div style={{
            background: 'var(--color-lavender)',
            padding: '24px',
            marginBottom: '24px'
          }}>
            <div className="body-regular" style={{ marginBottom: '8px' }}>
              Payment Details:
            </div>
            <div className="heading-3" style={{ marginBottom: '8px' }}>
              {paymentData.amount_crypto} {paymentData.cryptocurrency}
            </div>
            <div className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              ≈ ${paymentData.amount_usd} USD
            </div>
          </div>

          <div className="body-regular" style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
            Transaction ID: {paymentData.payment_id}
          </div>

          <div style={{
            background: 'var(--bg-secondary)',
            padding: '16px',
            marginBottom: '24px'
          }}>
            <div className="body-small" style={{ color: 'var(--text-secondary)' }}>
              What happens next:
            </div>
            <ul style={{ 
              listStyle: 'none', 
              padding: 0,
              margin: '12px 0 0 0',
              textAlign: 'left'
            }}>
              <li className="body-small" style={{ 
                marginBottom: '8px',
                paddingLeft: '20px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px',
                  background: 'var(--color-fence-green)',
                  borderRadius: '50%'
                }}></span>
                Confirmation email sent to {customerEmail}
              </li>
              <li className="body-small" style={{ 
                marginBottom: '8px',
                paddingLeft: '20px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px',
                  background: 'var(--color-fence-green)',
                  borderRadius: '50%'
                }}></span>
                {orderType === 'subscription' ? 'Subscription activated' : 'Order processing begins'}
              </li>
              <li className="body-small" style={{ 
                paddingLeft: '20px',
                position: 'relative'
              }}>
                <span style={{
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: '6px',
                  height: '6px',
                  background: 'var(--color-fence-green)',
                  borderRadius: '50%'
                }}></span>
                You will receive updates on order status
              </li>
            </ul>
          </div>

          <Button 
            className="btn-gold"
            onClick={() => window.location.href = '/'}
          >
            Return to Home
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
};